import { useCallback, useEffect, useRef, type TouchEvent as ReactTouchEvent } from 'react';
import { gsap } from 'gsap';
import { useReducedMotion } from './useReducedMotion';

/*
 * Slide-up overlay interaction, ported verbatim (constants + feel) from the
 * Digital Concierge `GuestShell.vue` and pinned in design-spec §7. See ADR-0006.
 *
 *   - Enter: y 100% → 0 over 0.9 s, power3.inOut.
 *   - Drag-to-dismiss on viewports ≤ 1024 px: only the top 80 px ("grip zone")
 *     starts a drag; resistance makes it feel heavier the further you pull;
 *     past 120 px it animates out and closes, otherwise it snaps back.
 *   - ≥ 1024 px relies on the back button (no drag) — handled by the consumer.
 *   - prefers-reduced-motion collapses enter/close to ~instant.
 */

const CLOSE_THRESHOLD = 120; // px dragged to trigger close
const DRAG_ZONE_HEIGHT = 80; // px from the top of the overlay that starts a drag
const DRAG_BREAKPOINT = 1024; // px — at/below this width, drag-to-dismiss is enabled

export function useSlideOverlay(onClose: () => void) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();

  const dragging = useRef(false);
  const dragStartY = useRef(0);
  const currentDragY = useRef(0);
  // Latest reducedMotion without re-binding handlers.
  const reducedRef = useRef(reducedMotion);
  reducedRef.current = reducedMotion;

  // Entry animation on mount.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    if (reducedRef.current) {
      gsap.set(el, { y: '0%' });
      return;
    }
    const tween = gsap.fromTo(
      el,
      { y: '100%' },
      { y: '0%', duration: 0.9, ease: 'power3.inOut' },
    );
    return () => {
      tween.kill();
    };
  }, []);

  // Animate out, then hand control back to the consumer to unmount.
  const requestClose = useCallback(() => {
    const el = overlayRef.current;
    if (!el || reducedRef.current) {
      onClose();
      return;
    }
    gsap.to(el, {
      y: '100%',
      opacity: 0,
      duration: 0.35,
      ease: 'power3.in',
      onComplete: onClose,
    });
  }, [onClose]);

  const onTouchStart = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    if (window.innerWidth > DRAG_BREAKPOINT) return;
    const el = overlayRef.current;
    const touch = e.touches[0];
    if (!el || !touch) return;
    const rect = el.getBoundingClientRect();
    const relativeY = touch.clientY - rect.top;
    if (relativeY > DRAG_ZONE_HEIGHT) return; // only the grip zone starts a drag
    dragging.current = true;
    dragStartY.current = touch.clientY;
    currentDragY.current = 0;
  }, []);

  const onTouchMove = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    const delta = touch.clientY - dragStartY.current;
    if (delta < 0) return; // never drag upward
    currentDragY.current = delta;

    if (backdropRef.current) {
      backdropRef.current.style.opacity = String(Math.max(0, 1 - delta / 400));
    }
    if (overlayRef.current) {
      const resisted = delta * (1 - delta / (window.innerHeight * 2));
      overlayRef.current.style.transform = `translateY(${Math.max(0, resisted)}px)`;
      overlayRef.current.style.opacity = String(Math.max(0.4, 1 - delta / 400));
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    const el = overlayRef.current;

    if (currentDragY.current >= CLOSE_THRESHOLD) {
      if (el) {
        gsap.to(el, { y: '100%', opacity: 0, duration: 0.35, ease: 'power3.in', onComplete: onClose });
      } else {
        onClose();
      }
    } else if (el) {
      // Snap back to rest.
      gsap.to(el, {
        y: 0,
        opacity: 1,
        duration: 0.4,
        ease: 'power3.out',
        clearProps: 'transform,opacity',
      });
      if (backdropRef.current) {
        gsap.to(backdropRef.current, { opacity: 1, duration: 0.4, ease: 'power3.out' });
      }
    }
    currentDragY.current = 0;
  }, [onClose]);

  return {
    overlayRef,
    backdropRef,
    requestClose,
    dragHandlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
}
